/**
 * KB.Icons.js
 *
 * Inline SVG icon registry for the Knowledgebase extension.
 *
 * Icons are monoline, single-color (currentColor), 24×24 viewBox.
 * Designed for an industrial blast & coatings supplier — sharp, technical,
 * authored to read at 14–80px without losing weight.
 *
 * Public API:
 *   KB.Icons.get(key, sizeClass)   — returns inline SVG markup as a string.
 *                                    Falls back to 'industry' if key is unknown.
 *   KB.Icons.normalize(name)       — coerce an admin-supplied string (could
 *                                    be an emoji, name, or empty) into one of
 *                                    the known keys.
 *
 * The normalize() function lets the existing admin field keep accepting
 * arbitrary strings (including legacy emojis like 💥 / 💦); we map them onto
 * the closest icon. Topics whose icon doesn't match anything render the
 * generic "industry" gear.
 */
define('KB.Icons', [], function () {
    'use strict';

    // ─── Icon path data — keep these tight, geometric, technical ───
    // All paths assume fill="none" stroke="currentColor" stroke-width="1.75"
    // with stroke-linecap="square" stroke-linejoin="miter" unless a glyph
    // specifically needs round caps (e.g. circles).
    var ICONS = {

        // BLAST — abrasive blast nozzle + angular media bursts.
        // The nozzle body sits on the left; the venturi opening flares
        // right with five staggered abrasive trajectories.
        blast:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              // hose stub
              '<path d="M2 12 L4 12" />' +
              // nozzle body (tapered cylinder)
              '<path d="M4 9 L9 9 L11 12 L9 15 L4 15 Z" />' +
              // primary blast jet (centre line)
              '<path d="M12 12 L22 12" />' +
              // upper trajectory + particle
              '<path d="M13 10 L20 6" />' +
              '<circle cx="21" cy="5.5" r="0.6" fill="currentColor" stroke="none" />' +
              // lower trajectory + particle
              '<path d="M13 14 L20 18" />' +
              '<circle cx="21" cy="18.5" r="0.6" fill="currentColor" stroke="none" />' +
              // inner-mid trajectories
              '<path d="M14 11 L19 9" />' +
              '<path d="M14 13 L19 15" />' +
            '</svg>',

        // SPRAY — side-profile spray gun with an HVLP-style atomized fan
        // pattern radiating right. Trigger guard is the diagnostic detail
        // that says "spray gun, not blast nozzle".
        spray:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              // gun body (barrel + grip)
              '<path d="M3 8 L11 8 L11 13 L9 13 L9 19 L4 19 L4 13 L3 13 Z" />' +
              // trigger guard
              '<path d="M5 14 L5 17 L8 17 L8 13" />' +
              // air-cap nozzle output
              '<path d="M11 9.5 L13 9.5 L13 11.5 L11 11.5" />' +
              // spray fan — five rays expanding outward
              '<path d="M14 10.5 L22 6" />' +
              '<path d="M14 10.5 L22 10.5" />' +
              '<path d="M14 10.5 L22 15" />' +
              // mid-fan droplets
              '<circle cx="20" cy="8" r="0.55" fill="currentColor" stroke="none" />' +
              '<circle cx="19" cy="13" r="0.55" fill="currentColor" stroke="none" />' +
            '</svg>',

        // WRENCH — open-end wrench at a 30° angle. Generic equipment/parts.
        wrench:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M15.5 4.5 L18.5 7.5 L17 9 L18 10 L20 8 A4 4 0 1 1 16 4 L14 6 L15 7 L16.5 5.5 Z" />' +
              '<path d="M14 9 L4.5 18.5 L6.5 20.5 L16 11" />' +
            '</svg>',

        // TOOL — crossed wrench + screwdriver. "Service/maintenance" generic.
        tool:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              // screwdriver shaft + handle (top-left → bottom-right)
              '<path d="M4 4 L7 4 L7 8 L18 19 L20 19 L20 21 L18 21 L18 19" />' +
              // wrench head (top-right) + shaft going bottom-left
              '<path d="M20 7 L17 4 L15 6 L17 8 L15 10 L13 8 L11 10 L4 17 L7 20 L14 13 Z" />' +
            '</svg>',

        // DOCUMENT — a sheet of paper with three text lines. Generic article.
        document:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              // sheet with folded corner
              '<path d="M5 3 L15 3 L19 7 L19 21 L5 21 Z" />' +
              '<path d="M15 3 L15 7 L19 7" />' +
              // text lines
              '<path d="M8 11 L16 11" />' +
              '<path d="M8 14 L16 14" />' +
              '<path d="M8 17 L13 17" />' +
            '</svg>',

        // QUESTION — circled question mark. Used for FAQ topics.
        question:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<circle cx="12" cy="12" r="9" />' +
              '<path d="M9 9 A3 3 0 1 1 12 13 L12 15" />' +
              '<circle cx="12" cy="18" r="0.7" fill="currentColor" stroke="none" />' +
            '</svg>',

        // GUIDE / BOOK — open book. "Reference guide" fallback for long-form.
        guide:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M3 5 L11 5 L12 7 L12 20 L11 19 L3 19 Z" />' +
              '<path d="M21 5 L13 5 L12 7 L12 20 L13 19 L21 19 Z" />' +
              '<path d="M5 9 L9 9" />' +
              '<path d="M5 13 L9 13" />' +
              '<path d="M15 9 L19 9" />' +
              '<path d="M15 13 L19 13" />' +
            '</svg>',

        // SAFETY — exclamation in a triangle. Compliance/safety topic.
        safety:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M12 3 L22 20 L2 20 Z" />' +
              '<path d="M12 10 L12 14" />' +
              '<circle cx="12" cy="17" r="0.7" fill="currentColor" stroke="none" />' +
            '</svg>',

        // GAUGE — pressure gauge / spec reading. Engineering/specs topic.
        gauge:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<circle cx="12" cy="12" r="9" />' +
              '<path d="M12 12 L17 8" />' +
              '<circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />' +
              '<path d="M4 12 L5.5 12" />' +
              '<path d="M18.5 12 L20 12" />' +
              '<path d="M12 4 L12 5.5" />' +
              '<path d="M12 18.5 L12 20" />' +
            '</svg>',

        // INDUSTRY — gear. Generic fallback for anything that doesn't map.
        industry:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M12 2 L13.4 4.6 L16 4 L16.6 6.6 L19 7.4 L18 10 L19.6 12 L18 14 L19 16.6 L16.6 17.4 L16 20 L13.4 19.4 L12 22 L10.6 19.4 L8 20 L7.4 17.4 L5 16.6 L6 14 L4.4 12 L6 10 L5 7.4 L7.4 6.6 L8 4 L10.6 4.6 Z" />' +
              '<circle cx="12" cy="12" r="3" />' +
            '</svg>',

        // ARROW-RIGHT — for "Read article" CTAs and forward navigation.
        arrowRight:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M4 12 L20 12" />' +
              '<path d="M14 6 L20 12 L14 18" />' +
            '</svg>',

        // ARROW-LEFT — back nav.
        arrowLeft:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M20 12 L4 12" />' +
              '<path d="M10 6 L4 12 L10 18" />' +
            '</svg>',

        // SEARCH — magnifying glass with squared handle (matches the
        // square stroke linecap of the rest of the system).
        search:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<circle cx="11" cy="11" r="6" />' +
              '<path d="M15.5 15.5 L20 20" />' +
            '</svg>',

        // PLUS / MINUS for the FAQ accordion toggles.
        plus:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M12 5 L12 19" />' +
              '<path d="M5 12 L19 12" />' +
            '</svg>',

        minus:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M5 12 L19 12" />' +
            '</svg>',

        // CHEVRON-DOWN — collapsed indicator alternative.
        chevronDown:
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">' +
              '<path d="M5 9 L12 16 L19 9" />' +
            '</svg>'
    };

    // ─── Normalization map ─────────────────────────────────────────
    // Accept the legacy emoji values, common english names, and short codes.
    // Anything not in this map falls back to 'industry'.
    var ALIASES = {
        // explicit slug-style keys (what admin should switch to)
        'blast': 'blast', 'sandblast': 'blast', 'abrasive': 'blast', 'nozzle': 'blast',
        'spray': 'spray', 'paint': 'spray', 'coating': 'spray', 'gun': 'spray',
        'wrench': 'wrench', 'tools': 'tool', 'tool': 'tool', 'parts': 'wrench',
        'document': 'document', 'article': 'document', 'doc': 'document',
        'question': 'question', 'faq': 'question', 'faqs': 'question',
        'guide': 'guide', 'reference': 'guide', 'book': 'guide', 'manual': 'guide',
        'safety': 'safety', 'warn': 'safety', 'warning': 'safety',
        'gauge': 'gauge', 'pressure': 'gauge', 'spec': 'gauge', 'specs': 'gauge',
        'industry': 'industry', 'gear': 'industry', 'settings': 'industry',

        // legacy emoji → best-match icon
        '💥': 'blast', '💣': 'blast', '🔥': 'blast', '⚡': 'blast', '✨': 'blast',
        '💦': 'spray', '🎨': 'spray', '💧': 'spray', '🖌': 'spray', '🖌️': 'spray',
        '🔧': 'wrench', '🔩': 'wrench',
        '🛠': 'tool', '🛠️': 'tool', '⚙': 'industry', '⚙️': 'industry',
        '📄': 'document', '📃': 'document', '📑': 'document',
        '❓': 'question', '❔': 'question', '🤔': 'question',
        '📖': 'guide', '📘': 'guide', '📚': 'guide',
        '⚠': 'safety', '⚠️': 'safety', '☣': 'safety', '☢': 'safety',
        '📊': 'gauge', '📈': 'gauge', '🏭': 'industry'
    };

    function normalize(raw) {
        if (!raw && raw !== 0) return 'industry';
        var s = String(raw).trim().toLowerCase();
        if (!s) return 'industry';
        if (ALIASES.hasOwnProperty(s)) return ALIASES[s];
        // Try the raw value as a direct key match (e.g. "blast" passed as-is)
        if (ICONS.hasOwnProperty(s)) return s;
        // Also try the original unlower-cased emoji (case shouldn't matter
        // for emojis but JS lowercases their text representation oddly)
        if (ALIASES.hasOwnProperty(String(raw).trim())) {
            return ALIASES[String(raw).trim()];
        }
        return 'industry';
    }

    function get(key, sizeClass) {
        var resolved = normalize(key);
        var svg = ICONS[resolved] || ICONS.industry;
        if (sizeClass) {
            // Inject a className attribute for sizing/coloring hooks
            svg = svg.replace('<svg ', '<svg class="' + sizeClass + '" ');
        }
        return svg;
    }

    return {
        get: get,
        normalize: normalize,
        // Expose for direct lookup if a caller wants the raw bag
        _icons: ICONS
    };
});
